/**
 * AARKA Lens Experience — Scroll-driven polarization simulation
 * Transitions an image from washed-out/glary to vivid/clear as user scrolls.
 * Uses GSAP ScrollTrigger for pinning and scroll-driven filter updates.
 */
if (!customElements.get('aarka-lens-experience')) {
  customElements.define('aarka-lens-experience', class extends HTMLElement {

    connectedCallback() {
      if (window.AARKA && window.AARKA.ready) {
        this.init();
      } else {
        document.addEventListener('aarka:scroll-ready', () => this.init(), { once: true });
      }
    }

    disconnectedCallback() {
      if (this.st) this.st.kill();
    }

    init() {
      var ScrollTrigger = window.AARKA.ScrollTrigger;

      this.wrapper = this.querySelector('.ale-wrapper');
      this.sticky = this.querySelector('.ale-sticky');
      this.images = this.querySelectorAll('.ale-media img, .ale-media .placeholder-svg');
      this.labelStart = this.querySelector('.ale-label--start');
      this.labelEnd = this.querySelector('.ale-label--end');
      this.flare = this.querySelector('.ale-flare');
      this.progressFill = this.querySelector('.ale-progress__fill');
      this.overlay = this.querySelector('.ale-overlay');

      if (!this.wrapper) return;

      /* Read configurable before-state values from data attributes */
      var beforeBrightness = parseFloat(this.dataset.beforeBrightness) || 1.3;
      var beforeContrast = parseFloat(this.dataset.beforeContrast) || 0.7;
      var beforeSaturation = parseFloat(this.dataset.beforeSaturation) || 0.5;
      var overlayOpacity = parseFloat(this.dataset.overlayOpacity) || 0.15;

      /* After state targets (normal vivid image) */
      var afterBrightness = 1.0;
      var afterContrast = 1.15;
      var afterSaturation = 1.3;

      var self = this;

      this.st = ScrollTrigger.create({
        trigger: this.wrapper,
        start: 'top top',
        end: 'bottom bottom',
        pin: this.sticky,
        scrub: 0.5,
        onUpdate: function(st) {
          var p = st.progress;

          // CSS filter: before → after (lerp between configurable start and vivid end)
          var brightness = beforeBrightness + (afterBrightness - beforeBrightness) * p;
          var contrast = beforeContrast + (afterContrast - beforeContrast) * p;
          var saturate = beforeSaturation + (afterSaturation - beforeSaturation) * p;
          var filterValue = 'brightness(' + brightness.toFixed(3) + ') contrast(' + contrast.toFixed(3) + ') saturate(' + saturate.toFixed(3) + ')';

          for (var i = 0; i < self.images.length; i++) {
            self.images[i].style.filter = filterValue;
          }

          // Overlay fades out from configured opacity to 0
          if (self.overlay) {
            self.overlay.style.opacity = (overlayOpacity * (1 - p)).toFixed(3);
          }

          // Glare flare — visible at start, fades by 50%
          if (self.flare) {
            if (p < 0.5) {
              self.flare.classList.add('ale-flare--visible');
              self.flare.style.opacity = ((1 - p / 0.5) * 0.8).toFixed(3);
            } else {
              self.flare.classList.remove('ale-flare--visible');
              self.flare.style.opacity = '0';
            }
          }

          // Start label: visible 0–45%, holds 5–30%, fades 30–45%
          if (self.labelStart) {
            if (p < 0.45) {
              var startOpacity = p < 0.05 ? (p / 0.05) : (p < 0.3 ? 1 : 1 - ((p - 0.3) / 0.15));
              self.labelStart.classList.toggle('ale-label--visible', startOpacity > 0.01);
              self.labelStart.style.opacity = Math.max(0, startOpacity).toFixed(3);
            } else {
              self.labelStart.classList.remove('ale-label--visible');
              self.labelStart.style.opacity = '0';
            }
          }

          // End label: fades in 55–75%, holds 75–100%
          if (self.labelEnd) {
            if (p > 0.55) {
              var endOpacity = p < 0.75 ? ((p - 0.55) / 0.2) : 1;
              self.labelEnd.classList.toggle('ale-label--visible', endOpacity > 0.01);
              self.labelEnd.style.opacity = Math.max(0, endOpacity).toFixed(3);
            } else {
              self.labelEnd.classList.remove('ale-label--visible');
              self.labelEnd.style.opacity = '0';
            }
          }

          // Progress bar
          if (self.progressFill) {
            self.progressFill.style.width = (p * 100).toFixed(1) + '%';
          }
        }
      });
    }
  });
}
